import { TableProps } from "interfaces/LightThemeTable";
import React, { ChangeEvent } from "react";
import Loader from "./Loader";
import NoDataAvailable from "./NoDataLightTheme";

import { Box, Grid } from "@material-ui/core";
import DESC_ICON from "assets/img/arrow-down.svg";
import ASC_ICON from "assets/img/arrow-up.svg";
import SEARCH_ICON from "assets/img/filter-data.svg";

export interface TableState {
  currentPage: number;
  pageSize: number;
  gotoPage: string;

  sortField: string;
  sortOrder: "asc" | "desc";

  activeFilterColumn: string | null;

  // search text inside currently open dropdown
  filterText: string;

  // filtered data (after applying filters)
  filteredData: any[];

  // applied filters: { fieldKey: ['val1','val2'] }
  filters: Record<string, string[]>;

  // temporary selections while dropdown is open: { fieldKey: Set(vals) }
  tempSelections: Record<string, Set<string>>;
}

export class LightThemeTable<
  T extends { id?: string | number; [key: string]: any }
> extends React.Component<TableProps<T>, TableState> {
  static defaultProps = {
    pageSizeOptions: [10, 50, 100, 200],
    defaultPageSize: 100,
  };

  constructor(props: TableProps<T>) {
    super(props);
    this.state = {
      currentPage: props.currentPage || 1,
      pageSize: props.defaultPageSize || 100,
      gotoPage: "",

      sortField: "",
      sortOrder: "asc",

      activeFilterColumn: null,
      filterText: "",

      filteredData: props.data ?? [],

      filters: {},
      tempSelections: {},
    };
  }

  componentDidUpdate(prevProps: TableProps<T>) {
    if ( typeof this.props.currentPage === "number" && this.props.currentPage !== prevProps.currentPage && this.props.currentPage !== this.state.currentPage) {
      this.setState({ currentPage: this.props.currentPage });
    }
    if (prevProps.data !== this.props.data) {
      // reinitialize filteredData and reapply any active filters
      const nextRows = this.props.data ?? [];
      this.setState({ filteredData: this.props.data ?? [] }, () => {
        this.applyAllFilters(true);
        if (typeof this.props.currentPage === "number" && nextRows.length > 0) {
          this.setState((prev) => {
            const maxPage = Math.max(1, Math.ceil(nextRows.length / prev.pageSize));
            if (prev.currentPage > maxPage) {
              this.props.onPageChange?.(maxPage, prev.pageSize);
              return { currentPage: maxPage };
            }
            return null;
          });
        }
      });
    }
  }

  getMaxPage = (rowsCount = this.state.filteredData.length, pageSize = this.state.pageSize) => {
    const total =
      this.props.totalRecords && this.props.totalRecords > 0
        ? this.props.totalRecords
        : rowsCount;
    return Math.max(1, Math.ceil(total / pageSize));
  };

  clampPage = (page: number, rowsCount = this.state.filteredData.length) => {
    const maxPage = this.getMaxPage(rowsCount, this.state.pageSize);
    return Math.max(1, Math.min(page, maxPage));
  };

  /* ----------------------- Sorting ------------------------ */

  handleSort = (field: string) => {
    let order: "asc" | "desc" = "asc";
    if (field === this.state.sortField) {
      order = this.state.sortOrder === "asc" ? "desc" : "asc";
    }
    this.setState({ sortField: field, sortOrder: order }, () => {
      this.props.onSortChange?.(field, order);
    });
  };

  sortData = (rows: T[]): T[] => {
    const { sortField, sortOrder } = this.state;
    if (!sortField || sortField === "") return rows;
    const isStoreNumberField = /store.*(number|num|no|id)$/i.test(sortField);
    return [...rows].sort((a, b) => {
      let x = a[sortField];
      let y = b[sortField];

      if (isStoreNumberField) {
        const sx = String(x ?? "").trim();
        const sy = String(y ?? "").trim();
        const nx = /^\d+$/.test(sx);
        const ny = /^\d+$/.test(sy);
        const result = nx && ny ? Number(sx) - Number(sy) : nx !== ny ? nx ? -1 : 1 : sx.localeCompare(sy, undefined, { numeric: true, sensitivity: "base" });
        return sortOrder === "asc" ? result : -result;
      }

      if (typeof x === "string") x = x.toLowerCase();
      if (typeof y === "string") y = y.toLowerCase();
      if (x < y) return sortOrder === "asc" ? -1 : 1;
      if (x > y) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  /* ----------------------- Filter dropdown lifecycle ------------------------ */

  openFilter = (column: string, e: any) => {
    e.stopPropagation();
    const fieldKey = String(column);
    const opening = this.state.activeFilterColumn !== fieldKey;
    if (!opening) {
      this.setState({ activeFilterColumn: null, filterText: "" });
      return;
    }

    const applied = this.state.filters[fieldKey] ?? [];
    const temp = new Set<string>(applied);

    this.setState((prev) => ({
      activeFilterColumn: fieldKey,
      filterText: "",
      tempSelections: { ...prev.tempSelections, [fieldKey]: temp },
    }));
  };

  handleFilterInput = (e: any) => {
    this.setState({ filterText: e.target.value });
  };

  toggleTempSelection = (fieldKey: string, val: string) => {
    this.setState((prev) => {
      const tempSelections = { ...prev.tempSelections };
      const setForCol = new Set<string>(tempSelections[fieldKey] ?? []);
      if (setForCol.has(val)) setForCol.delete(val);
      else setForCol.add(val);
      tempSelections[fieldKey] = setForCol;
      return { tempSelections };
    });
  };

  applyTempSelections = () => {
    const col = this.state.activeFilterColumn;
    if (!col) return;
    this.setState(
      (prev) => {
        const sel = Array.from(prev.tempSelections[col] ?? []);
        const newFilters = { ...prev.filters };
        if (sel.length > 0) newFilters[col] = sel;
        else delete newFilters[col];

        return {
          filters: newFilters,
          activeFilterColumn: null,
          filterText: "",
          currentPage: 1,
        };
      },
      () => this.applyAllFilters()
    );
  };

  cancelTempSelections = () => {
    const col = this.state.activeFilterColumn;
    if (!col) {
      this.setState({ filterText: "" });
      return;
    }
    this.setState((prev) => {
      const applied = prev.filters[col] ?? [];
      const tempSelections = { ...prev.tempSelections, [col]: new Set<string>(applied) };
      return { tempSelections, activeFilterColumn: null, filterText: "" };
    });
  };

  clearFilterForColumn = (column: string) => {
    const fieldKey = String(column);
    this.setState(
      (prev) => {
        const newFilters = { ...prev.filters };
        delete newFilters[fieldKey];
        const newTemp = { ...prev.tempSelections };
        newTemp[fieldKey] = new Set<string>();
        return {
          filters: newFilters,
          tempSelections: newTemp,
          filterText: "",
          currentPage: 1,
        };
      },
      () => this.applyAllFilters()
    );
  };

  clearAllFilters = () => {
    this.setState(
      { filters: {}, tempSelections: {}, filterText: "", activeFilterColumn: null, currentPage: 1 },
      () => this.applyAllFilters()
    );
  };

  /* ----------------------- Apply filters to data ------------------------ */

  applyAllFilters = (preservePage = false) => {
    const data = this.props.data ?? [];
    const { filters } = this.state;
    const isPageControlled = typeof this.props.currentPage === "number";

    const filterKeys = Object.keys(filters);
    if (filterKeys.length === 0) {
      if (preservePage) {
        this.setState((prev) => {
          const maxPage = Math.max(1, Math.ceil(data.length / prev.pageSize));
          return {
            filteredData: data,
            currentPage: isPageControlled
              ? prev.currentPage
              : Math.max(1, Math.min(prev.currentPage, maxPage)),
          };
        });
      } else this.setState({ filteredData: data, currentPage: 1 });
      return;
    }

    const filtered = data.filter((row) => {
      // across columns -> AND: every column's filter must match
      return filterKeys.every((col) => {
        const selVals = filters[col] ?? [];
        if (selVals.length === 0) return true;

        const cell = row[col];

        // If cell is array -> match if any selected value is found in the array (OR within column)
        if (Array.isArray(cell)) {
          const arrNorm = cell.map((x) => String(x ?? "").toLowerCase());
          return selVals.some((v) => arrNorm.includes(String(v).toLowerCase()));
        }

        // Primitive cell -> match if cell equals any selected value (OR within column)
        const cellStr = String(cell ?? "").toLowerCase();
        return selVals.some((v) => cellStr === String(v).toLowerCase());
      });
    });

    if (preservePage) {
      this.setState((prev) => {
        const maxPage = Math.max(1, Math.ceil(filtered.length / prev.pageSize));
        return {
          filteredData: filtered,
          currentPage: isPageControlled
            ? prev.currentPage
            : Math.max(1, Math.min(prev.currentPage, maxPage)),
        };
      });
    } else this.setState({ filteredData: filtered, currentPage: 1 });
  };

  /* ----------------------- Pagination handlers ------------------------ */

  // get totalPages() {
  //   const { filteredData, pageSize } = this.state;
  //   const total = filteredData.length;
  //   return Math.max(1, Math.ceil(total / pageSize));
  // }
  get totalPages() {
    // Use totalRecords prop if available, otherwise fall back to filteredData length
    const total =
      this.props.totalRecords && this.props.totalRecords > 0
        ? this.props.totalRecords
        : this.state.filteredData.length;
    return Math.max(1, Math.ceil(total / this.state.pageSize));
  }

  handlePageChange = (page: number) => {
    const newPage = this.clampPage(page);
    this.setState({ currentPage: newPage }, () => {
      this.props.onPageChange?.(newPage, this.state.pageSize);
    });
  };

  handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    this.setState(
      (prev) => {
        const maxPage = this.getMaxPage(prev.filteredData.length, newSize);
        return { pageSize: newSize, currentPage: Math.min(prev.currentPage, maxPage) };
      },
      () => {
        this.props.onPageChange?.(this.state.currentPage, this.state.pageSize);
      }
    );
  };

  handleGotoPage = () => {
    const pageNumber = parseInt(this.state.gotoPage, 10);
    if (!isNaN(pageNumber)) {
      this.handlePageChange(pageNumber);
      this.setState({ gotoPage: "" });
    }
  };

  render() {
    const { tableHeight, showPagination = true } = this.props || "400px";
    const bodyWrapperStyle: React.CSSProperties = tableHeight
      ? {
          minHeight: "225px",
          // height: tableHeight,
          background: "white",
          // maxHeight: typeof tableHeight === "number" ? `${tableHeight}px` : tableHeight,
          // height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }
      : { overflowY: "auto",
          minHeight: "225px",
          background: "white",
       };
    const { columns, pageSizeOptions, onClickOnRow } = this.props;
    const {
      currentPage,
      pageSize,
      gotoPage,
      sortField,
      sortOrder,
      activeFilterColumn,
      filterText,
      filteredData,
      filters,
      tempSelections,
    } = this.state;

    // ensure filteredData initialized
    const dataForRender = filteredData ?? this.props.data ?? [];
    const sorted = this.sortData(dataForRender);
    const startIndex = (currentPage - 1) * pageSize;
    const displayData = sorted.slice(startIndex, startIndex + pageSize);

    const getPageNumbers = () => {
      const total = this.totalPages;
      const current = currentPage;
      const pages: (number | string)[] = [];
      if (total <= 3) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        pages.push(1, 2);
        if (current >= 3 && current < total - 1) {
          pages.push("...", current, "...");
        } else if (current >= total - 1) {
          pages.push("...", total - 1);
        } else {
          pages.push("...");
        }
        pages.push(total);
      }
      return pages;
    };

    return (
      <div style={{ width: "100%" }}>
        <div style={bodyWrapperStyle}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "600px",
              // minHeight: "200px",
              // background: "pink",
            }}
          >
            <thead style={{ backgroundColor: "#F9FAFB" }}>
              <tr>
                {columns.map((col) => {
                  const fieldKey = String(col.field);
                  const isSorted = sortField === col.field;
                  const isFilterOpen = activeFilterColumn === fieldKey;
                  const appliedCount = (filters[fieldKey] ?? []).length;

                  // build unique options for this column from props.data
                  const uniqueVals = Array.from(
                    new Set(
                      (this.props.data ?? []).flatMap((d) => {
                        const v = d[fieldKey];
                        if (Array.isArray(v)) return v.map((x) => String(x ?? "-"));
                        return String(v ?? "-");
                      })
                    )
                  ).sort((a, b) => String(a).localeCompare(String(b)));

                  const optionsToShow = uniqueVals.filter((val) =>
                    val.toLowerCase().includes((filterText ?? "").toLowerCase())
                  );

                  const tempSet = tempSelections[fieldKey] ?? new Set<string>();

                  return (
                    <th
                      key={fieldKey}
                      style={{
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        textAlign: "left",
                        padding: "12px 10px",
                        borderBottom: "2px solid #E3E6EA",
                        fontWeight: "bold",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        userSelect: "none",
                        position: "relative",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                        {/* show icons only when header is a non-empty string (A1) */}
                        {col.header && String(col.header).trim() !== "" && (
                          <>
                            {String(col.header).toLowerCase().includes("logo") ||
                            // String(col.header).toLowerCase().includes("store state") ||
                            String(col?.field).toLowerCase().includes("checkbox") ||
                            // String(col?.field).toLowerCase().includes("stores") ||
                            String(col?.field).toLowerCase().includes("action") ||
                            String(col?.field).toLowerCase().includes("qr_code") ||
                            String(col?.field).toLowerCase().includes("reportaccess") ||
                            String(col?.field).toLowerCase().includes("storecount") ||
                            String(col?.field).toLowerCase().includes("cubicshare") ||
                            String(col?.field).toLowerCase().includes("shareshareprior") ||
                            String(col?.field).toLowerCase().includes("sharechange") ||
                            String(col?.field).toLowerCase().includes("adds") ||
                            String(col?.field).toLowerCase().includes("deletes") ||
                            String(col?.field).toLowerCase().includes("maintains") ||
                            String(col?.field).toLowerCase().includes("totalpods") ||
                            String(col?.field).toLowerCase().includes("podchange") ||
                            String(col?.field).toLowerCase().includes("qr_code") ||
                            String(col?.field).toLowerCase().includes("display_request_id") ||
                            String(col?.field).toLowerCase().includes("display_request_id") ||
                            String(col?.field).toLowerCase().includes("request_initiator_email") ||
                            String(col?.field).includes("CreatedAt") ||
                            String(col?.header).includes("Request Status") ||
                            String(col?.field).toLowerCase().includes("request_text") ||
                            String(col?.field).toLowerCase().includes("addsperstore") ? (
                              // Logo: just text
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {col.header}
                              </span>
                            ) : String(col.header).toLowerCase().includes("status") ||
                            String(col?.field).toLowerCase().includes("user_completed") ? (
                              // Status: text + sort icon
                              <span
                                style={{
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                                onClick={() => this.handleSort(fieldKey)}
                              >
                                {col.header}
                                <img
                                  src={
                                    isSorted
                                      ? sortOrder === "asc"
                                        ? ASC_ICON
                                        : DESC_ICON
                                      : ASC_ICON
                                  }
                                  style={{ width: 10, opacity: isSorted ? 1 : 0.4, marginLeft: 6 }}
                                  alt="sort"
                                />
                              </span>
                            ) : (
                              // All other headers: text + sort + filter
                              <>
                                <span
                                  style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  onClick={() => this.handleSort(fieldKey)}
                                >
                                  {col.header}
                                  <img
                                    src={
                                      isSorted
                                        ? sortOrder === "asc"
                                          ? ASC_ICON
                                          : DESC_ICON
                                        : ASC_ICON
                                    }
                                    style={{
                                      width: 10,
                                      opacity: isSorted ? 1 : 0.4,
                                      marginLeft: 6,
                                      flexShrink: 0,
                                    }}
                                    alt="sort"
                                  />
                                </span>
                                <img
                                  src={SEARCH_ICON}
                                  onClick={(e) => this.openFilter(fieldKey, e)}
                                  style={{
                                    width: 12,
                                    marginLeft: 8,
                                    cursor: "pointer",
                                    opacity: isFilterOpen ? 1 : 0.6,
                                    flexShrink: 0,
                                  }}
                                  alt="filter"
                                />
                              </>
                            )}
                          </>
                        )}
                        {/* clear badge */}
                        {appliedCount > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <button
                              onClick={() => this.clearFilterForColumn(fieldKey)}
                              style={{
                                backgroundColor: "#F3F4F6",
                                border: "1px solid #E5E7EB",
                                padding: "2px 6px",
                                borderRadius: 12,
                                fontSize: 11,
                                cursor: "pointer",
                              }}
                              title={`Clear ${appliedCount} filter(s)`}
                            >
                              Clear ({appliedCount})
                            </button>
                          </div>
                        )}
                      </div>

                      {/* filter dropdown */}
                      {isFilterOpen && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            background: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            padding: 10,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            zIndex: 999,
                            width: 260,
                          }}
                        >
                          <div
                            style={{ position: "relative", display: "flex", alignItems: "center" }}
                          >
                            <input
                              type="text"
                              placeholder="Search..."
                              value={filterText}
                              onChange={this.handleFilterInput}
                              style={{
                                width: "100%",
                                padding: "6px 28px 6px 8px",
                                fontSize: 12,
                                border: "1px solid #ccc",
                                borderRadius: 4,
                              }}
                            />
                            {filterText !== "" && (
                              <span
                                onClick={() => this.setState({ filterText: "" })}
                                style={{
                                  position: "absolute",
                                  right: 8,
                                  cursor: "pointer",
                                  fontSize: 14,
                                  color: "#555",
                                  userSelect: "none",
                                }}
                              >
                                ×
                              </span>
                            )}
                          </div>

                          <div
                            style={{
                              marginTop: 8,
                              maxHeight: 200,
                              overflowY: "auto",
                              borderTop: "1px solid #F3F4F6",
                              paddingTop: 8,
                            }}
                          >
                            {optionsToShow.length === 0 ? (
                              <div style={{ padding: "6px 8px", fontSize: 12, color: "#6B7280" }}>
                                No values
                              </div>
                            ) : (
                              optionsToShow.map((val) => {
                                const checked = tempSet.has(val);
                                return (
                                  <label
                                    key={val}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      padding: "6px 8px",
                                      cursor: "pointer",
                                      fontSize: 12,
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      style={{ accentColor: "#E5B42F" } as any}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        this.toggleTempSelection(fieldKey, val);
                                      }}
                                    />
                                    <span style={{ userSelect: "none" }}>{val}</span>
                                  </label>
                                );
                              })
                            )}
                          </div>

                          <div
                            style={{
                              marginTop: 8,
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 8,
                            }}
                          >
                            <button
                              onClick={this.applyTempSelections}
                              style={{
                                flex: 1,
                                padding: "6px 16px",
                                borderRadius: 4,
                                border: "none",
                                background: "#E5B42F",
                                color: "#000",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              Apply
                            </button>

                            <button
                              onClick={this.cancelTempSelections}
                              style={{
                                flex: 1,
                                backgroundColor: "#F3F4F6",
                                border: "1px solid #E5E7EB",
                                padding: "6px 8px",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: 12,
                              }}
                            >
                              Cancel
                            </button>

                            {/*<button*/}
                            {/*  onClick={() => {*/}
                            {/*    // clear temp & applied for this column*/}
                            {/*    this.setState((prev) => {*/}
                            {/*      const newTemp = { ...prev.tempSelections, [fieldKey]: new Set<string>() };*/}
                            {/*      const newFilters = { ...prev.filters };*/}
                            {/*      delete newFilters[fieldKey];*/}
                            {/*      return { tempSelections: newTemp, filters: newFilters, activeFilterColumn: null, filterText: "" };*/}
                            {/*    }, () => this.applyAllFilters());*/}
                            {/*  }}*/}
                            {/*  style={{*/}
                            {/*    backgroundColor: "#EF4444",*/}
                            {/*    color: "#fff",*/}
                            {/*    border: "none",*/}
                            {/*    padding: "6px 8px",*/}
                            {/*    borderRadius: 4,*/}
                            {/*    cursor: "pointer",*/}
                            {/*    fontSize: 12,*/}
                            {/*  }}*/}
                            {/*>*/}
                            {/*  Clear*/}
                            {/*</button>*/}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* <div style={{ height: "400px", width: "100%", background: "pink" }}> */}
            <tbody style={{ width: "100%", height: "100%" }}>
              {this.props.isLoading ? (
                <tr>
                  <td colSpan={columns.length} style={{ padding: 0 }}>
                    <Loader />
                  </td>
                </tr>
              ) : !this.props.isLoading && displayData.length > 0 ? (
                displayData.map((row, idx) => (
                  <tr
                    key={row.id || idx}
                    onClick={() => onClickOnRow?.(row)} // **NEW: Optional call to onClickOnRow**
                    style={{
                      backgroundColor: idx % 2 === 0 ? "#FFFFFF" : "#FEFCE8",
                      cursor: onClickOnRow ? "pointer" : "default",
                      padding: "5px 0",
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.header}
                        style={{
                          padding: "5px 12px",
                          maxWidth: col.maxWidth,
                          minWidth: col.minWidth,
                          border: "none",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          borderBottom: "2px solid #FDF19EC2",
                        }}
                      >
                        {col.formatter
                          ? col.formatter(row[col.field], row, idx)
                          : row[col.field] ?? "-"}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} style={{ padding: 0 }}>
                    {this.props.noDataMessage ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "350px", color: "#999", fontSize: "2rem", }}>
                        {this.props.noDataMessage}
                      </div>
                    ) : (
                    <NoDataAvailable />
                    )}
                  </td>
                </tr>
              )}
            </tbody>
            {/* </div> */}
          </table>
        </div>

        {displayData.length > 0 && showPagination && (
          <Grid container spacing={2} style={{ padding: "16px 8px" }}>
            {/* Page Size */}
            <Grid xs={12} md={4} spacing={1}>
              <label
                style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}
              >
                Rows per page:
                <select
                  value={pageSize}
                  onChange={this.handlePageSizeChange}
                  style={{
                    width: 60,
                    padding: "4px 6px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    textAlign: "center",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontSize: "12px",
                    // zIndex: 999,
                  }}
                >
                  {pageSizeOptions?.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </Grid>
            {/* Page Numbers */}
            <Grid xs={12} md={4}>
              <Box
                flex="1 1 auto"
                display="flex"
                justifyContent={{ xs: "flex-start", md: "center" }}
                alignItems="center"
              >
                <span
                  onClick={() => this.handlePageChange(currentPage - 1)}
                  style={{
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    color: currentPage === 1 ? "#ccc" : "#000",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  &lt;
                </span>

                {getPageNumbers().map((p, idx) =>
                  typeof p === "number" ? (
                    <span
                      key={idx}
                      onClick={() => this.handlePageChange(p as number)}
                      style={{
                        cursor: "pointer",
                        padding: "4px 8px",
                        fontSize: "12px",
                        borderBottom:
                          currentPage === p ? "2px solid #E5B42F" : "2px solid transparent",
                      }}
                    >
                      {p}
                    </span>
                  ) : (
                    <span
                      key={idx}
                      style={{ padding: "4px 8px", fontSize: "12px", cursor: "default" }}
                    >
                      {p}
                    </span>
                  )
                )}

                <span
                  onClick={() => this.handlePageChange(currentPage + 1)}
                  style={{
                    cursor: currentPage === this.totalPages ? "not-allowed" : "pointer",
                    color: currentPage === this.totalPages ? "#ccc" : "#000",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  &gt;
                </span>
              </Box>
            </Grid>
            {/* Go To Page */}
            <Grid xs={12} md={4}>
              <Box
                style={{ gap: "6px" }}
                display="flex"
                flexWrap="wrap"
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                alignItems="center"
                fontSize={12}
              >
                <span>Go To</span>
                <input
                  type="text"
                  value={gotoPage}
                  onChange={(e) => this.setState({ gotoPage: e.target.value })}
                  placeholder="Page"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") this.handleGotoPage();
                  }}
                  style={{
                    width: 60,
                    padding: "4px 6px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    textAlign: "center",
                    fontSize: "12px",
                  }}
                />
                <button
                  onClick={this.handleGotoPage}
                  style={{
                    backgroundColor: "#E5B42F",
                    border: "none",
                    padding: "5px 12px",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  Go
                </button>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Active filters summary & clear all */}
        {Object.keys(filters).length > 0 && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 12, color: "#374151", fontWeight: 800 }}>
              Clear All Applied Filters:
            </div>
            <button
              onClick={this.clearAllFilters}
              style={{
                background: "#E5B42F",
                color: "#000",
                border: "none",
                padding: "6px 10px",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    );
  }
}
